// Conocimiento del agente: documentos propios + skills de la org.
//
// · Documentos: se ligan por id (AssistantDocument). Orión acota la búsqueda a
//   ellos cuando el agente está activo (Orion-Brainstream services/assistant_scope.py).
//   Se pueden subir aquí mismo (quedan en «Mis documentos», privados) o elegir
//   de la Librería.
// · Skills: OrgWorkflow(kind="skill") activas de la org. El backend le dice a
//   Orión que las abra con get_workflow(slug) (apps/assistants/services.py).
// El componente solo edita la lista de ids/slugs del borrador; se guarda con
// «Guardar cambios» / «Crear agente».

import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen, Check, FileImage, FileSpreadsheet, FileText, Library, Loader2, Plus, Search, Upload, X,
} from 'lucide-react';

import { listDocuments, uploadDocument } from '@/modules/documents/api';
import type { DocumentOut } from '@/modules/documents/types';
import { DOCS_KEY, useOrgSkills } from './skillsApi';

const ACCEPT = '.pdf,.doc,.docx,.txt,.csv,.png,.jpg,.jpeg,.webp';
const MAX_BYTES = 20 * 1024 * 1024;

function DocIcon({ type }: { type?: string }) {
  if (type === 'image') return <FileImage size={15} aria-hidden />;
  if (type === 'csv') return <FileSpreadsheet size={15} aria-hidden />;
  return <FileText size={15} aria-hidden />;
}

function titleFromFilename(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim() || name;
}

function SectionTitle({ icon, title, hint, count }: { icon: JSX.Element; title: string; hint: string; count: number }) {
  return (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <span className="text-primary">{icon}</span>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {count > 0 && (
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-primary">{count}</span>
        )}
      </div>
      <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{hint}</p>
    </div>
  );
}

// ── Documentos ────────────────────────────────────────────────

export function AgentDocumentsField({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const qc = useQueryClient();
  const docs = useQuery<DocumentOut[]>({ queryKey: DOCS_KEY, queryFn: () => listDocuments({ tab: 'all', limit: 200 }) });
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [uploading, setUploading] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const byId = useMemo(() => new Map((docs.data ?? []).map((d) => [d.id, d])), [docs.data]);
  const linked = value.map((id) => byId.get(id) ?? ({ id, title: 'Documento', file_type: '' } as DocumentOut));
  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (docs.data ?? []).filter((d) => !value.includes(d.id) && (!q || d.title.toLowerCase().includes(q)));
  }, [docs.data, value, query]);

  const upload = async (files: FileList) => {
    setError(null);
    const list = Array.from(files);
    const tooBig = list.filter((f) => f.size > MAX_BYTES);
    if (tooBig.length) setError(`«${tooBig[0].name}» pesa más de 20 MB.`);
    const ok = list.filter((f) => f.size <= MAX_BYTES);
    setUploading((u) => [...u, ...ok.map((f) => f.name)]);
    const added: string[] = [];
    for (const f of ok) {
      try {
        // Privado al subirlo: el agente lo consulta aunque nadie más lo vea en la Librería.
        const d = await uploadDocument({ file: f, title: titleFromFilename(f.name), description: '', tags: [], visibility: 'personal' });
        added.push(d.id);
      } catch (e) {
        const msg = e instanceof Error ? e.message.replace(/^HTTP \d+: /, '') : 'error';
        setError(`No se pudo subir «${f.name}»: ${msg.slice(0, 160)}`);
      } finally {
        setUploading((u) => u.filter((n) => n !== f.name));
      }
    }
    if (added.length) {
      await qc.invalidateQueries({ queryKey: DOCS_KEY });
      onChange([...value, ...added.filter((id) => !value.includes(id))]);
    }
  };

  return (
    <section aria-label="Documentos del agente" className="rounded-2xl border border-border bg-background/60 p-3 sm:p-4">
      <SectionTitle
        icon={<BookOpen size={16} aria-hidden />}
        title="Documentos del agente"
        count={value.length}
        hint="Orión responde con estos documentos cuando usan este agente. Quien pueda usar el agente podrá consultar su contenido."
      />

      {linked.length > 0 && (
        <ul className="mb-2 grid gap-1.5">
          {linked.map((d) => (
            <li key={d.id} className="flex min-w-0 items-center gap-2 rounded-xl border border-border bg-card px-3 py-2">
              <span className="text-muted-foreground"><DocIcon type={d.file_type} /></span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">{d.title}</span>
              {d.embedding_status && d.embedding_status !== 'indexed' && (
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10.5px] text-muted-foreground">
                  {d.embedding_status === 'failed' ? 'No se pudo leer' : 'Procesando'}
                </span>
              )}
              <button
                type="button"
                onClick={() => onChange(value.filter((id) => id !== d.id))}
                aria-label={`Quitar ${d.title}`}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <X size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {uploading.map((n) => (
        <p key={n} role="status" className="mb-1.5 flex items-center gap-2 rounded-xl bg-muted/60 px-3 py-2 text-[13px] text-muted-foreground">
          <Loader2 size={14} className="animate-spin" aria-hidden /> Subiendo «{n}»…
        </p>
      ))}
      {error && <p role="alert" className="mb-2 rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) void upload(e.target.files);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-primary/10 px-3 text-[13px] font-medium text-primary hover:bg-primary/15"
        >
          <Upload size={15} aria-hidden /> Subir archivo
        </button>
        <button
          type="button"
          onClick={() => setPicking((v) => !v)}
          aria-expanded={picking}
          className="inline-flex h-9 items-center gap-1.5 rounded-xl border border-border bg-card px-3 text-[13px] font-medium text-foreground hover:bg-muted"
        >
          <Library size={15} aria-hidden /> Elegir de mis documentos
        </button>
      </div>
      <p className="mt-1.5 text-[11px] text-muted-foreground">PDF, Word, TXT, CSV o imagen · hasta 20 MB cada uno</p>

      {picking && (
        <div className="mt-3 rounded-xl border border-border bg-card p-2">
          <label className="relative block">
            <span className="sr-only">Buscar documento</span>
            <Search size={14} aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar en tus documentos…"
              className="h-9 w-full rounded-lg border border-input bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <ul className="mt-2 max-h-60 overflow-y-auto">
            {docs.isLoading && (
              <li className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
                <Loader2 size={14} className="animate-spin" aria-hidden /> Cargando documentos…
              </li>
            )}
            {docs.isError && <li className="px-2 py-3 text-xs text-destructive">No se pudieron cargar tus documentos.</li>}
            {!docs.isLoading && !docs.isError && available.length === 0 && (
              <li className="px-2 py-3 text-xs text-muted-foreground">
                {(docs.data ?? []).length === 0 ? 'Aún no tienes documentos. Usa «Subir archivo».' : 'No hay más documentos que coincidan.'}
              </li>
            )}
            {available.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  onClick={() => onChange([...value, d.id])}
                  className="flex w-full min-w-0 items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-muted"
                >
                  <span className="text-muted-foreground"><DocIcon type={d.file_type} /></span>
                  <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">{d.title}</span>
                  <Plus size={14} aria-hidden className="shrink-0 text-primary" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ── Skills ────────────────────────────────────────────────────

export function AgentSkillsField({ value, onChange, onManage }: {
  value: string[];
  onChange: (slugs: string[]) => void;
  onManage?: () => void;
}) {
  const skills = useOrgSkills();
  const active = (skills.data ?? []).filter((s) => s.status === 'active');
  // Ligadas que ya no están activas: se muestran para poder quitarlas.
  const orphan = value.filter((slug) => !active.some((s) => s.slug === slug));
  const toggle = (slug: string) => onChange(value.includes(slug) ? value.filter((s) => s !== slug) : [...value, slug]);

  return (
    <section aria-label="Skills del agente" className="rounded-2xl border border-border bg-background/60 p-3 sm:p-4">
      <SectionTitle
        icon={<Library size={16} aria-hidden />}
        title="Skills"
        count={value.length}
        hint="Procedimientos de tu organización que este agente sigue paso a paso cuando la tarea lo pide."
      />
      {skills.isLoading && (
        <p role="status" className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
          <Loader2 size={14} className="animate-spin" aria-hidden /> Cargando skills…
        </p>
      )}
      {skills.isError && <p className="py-2 text-xs text-destructive">No se pudieron cargar las skills.</p>}
      {!skills.isLoading && !skills.isError && active.length === 0 && orphan.length === 0 && (
        <p className="py-1 text-xs text-muted-foreground">
          Tu organización aún no tiene skills.{' '}
          {onManage && (
            <button type="button" onClick={onManage} className="font-semibold text-primary hover:underline">
              Ver la pestaña Skills
            </button>
          )}
        </p>
      )}
      {(active.length > 0 || orphan.length > 0) && (
        <ul className="grid gap-1.5 sm:grid-cols-2">
          {active.map((s) => {
            const on = value.includes(s.slug);
            return (
              <li key={s.slug}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={on}
                  onClick={() => toggle(s.slug)}
                  className={`flex h-full w-full items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                    on ? 'border-primary/50 bg-primary/10' : 'border-border bg-card hover:border-primary/30'
                  }`}
                >
                  <span
                    aria-hidden
                    className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border ${
                      on ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background'
                    }`}
                  >
                    {on && <Check size={11} strokeWidth={3} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[13px] font-medium text-foreground">{s.title}</span>
                    {(s.description || s.when_to_use) && (
                      <span className="mt-0.5 line-clamp-2 block text-[11.5px] leading-snug text-muted-foreground">
                        {s.description || s.when_to_use}
                      </span>
                    )}
                  </span>
                </button>
              </li>
            );
          })}
          {orphan.map((slug) => (
            <li key={slug} className="flex items-center gap-2 rounded-xl border border-dashed border-border px-3 py-2.5">
              <span className="min-w-0 flex-1 truncate text-[13px] text-muted-foreground">{slug} · ya no está activa</span>
              <button
                type="button"
                onClick={() => toggle(slug)}
                aria-label={`Quitar ${slug}`}
                className="grid h-7 w-7 place-items-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
              >
                <X size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
